import { TFunction } from 'i18next';
import { Dispatch, SetStateAction } from 'react';
import { apiClientWithoutHandler } from 'src/js/api';
import { APIPath, APIPathWithArguments } from 'src/js/api/route-path-index';
import { GenericResponse } from 'src/js/model/api/response/generic-response';
import { NotificationVariant } from 'src/js/notification/notification';
import { notificationHandler } from 'src/js/notification/notification-handler';
import { createAndDispatchNotification } from 'src/js/notification/notification-helper';
import { createWatchStore, updateWatchStore } from 'src/js/store/use-watch';

import {
  GetAllMetadataResponse,
  MetadataResponse,
  MetadataViewModel,
  mapMetadataResponse,
  mapMetadataViewModel,
} from './metadata-models';

const metadataModels: MetadataViewModel[] = [];
createWatchStore(metadataModels);
export default metadataModels;

export const saveResponseToMetadata = (response: MetadataViewModel[]) => {
  updateWatchStore(metadataModels, response);
};

const replaceViewModel = (response: MetadataResponse, t: TFunction, selected = false) => {
  const viewModel = mapMetadataViewModel(response, t);
  const metadata = [...metadataModels];
  const index = metadata.findIndex((e) => e.id === viewModel.id);
  if (index > -1) {
    metadata.splice(index, 1, { ...viewModel, selected: selected });
  }

  saveResponseToMetadata(metadata);
};

const deleteViewModel = (model: MetadataViewModel) => {
  const metadata = [...metadataModels];
  const index = metadata.findIndex((e) => e.id === model.id);
  if (index > -1) {
    metadata.splice(index, 1);
  }

  saveResponseToMetadata(metadata);
};

export const fetchMetadata = (t: TFunction) => {
  return apiClientWithoutHandler.genericController.get<GetAllMetadataResponse>(APIPath.metadata.list).then((res) => {
    if (res.ok) {
      const response = res.data;
      if (response) {
        const viewModels = mapMetadataResponse(response, t);
        saveResponseToMetadata(viewModels);
      }
    }
    return res;
  });
};

export const startStopMetadata = (model: MetadataViewModel, start: boolean, selected: boolean, t: TFunction) => {
  const path = APIPathWithArguments(start ? APIPath.metadata.start : APIPath.metadata.stop, {
    id: model.id,
  });
  return apiClientWithoutHandler.genericController
    .put<MetadataResponse>(path)
    .then((res) => {
      if (res.ok) {
        const response = res.data;
        if (response) {
          replaceViewModel(response, t, selected);
        }
      }
      return res;
    })
    .then((res) => {
      notificationHandler(res, null, null, t);
      return res;
    });
};

export const fetchMetadataStatistics = (
  model: MetadataViewModel,
  t: TFunction,
  setStatistics: Dispatch<SetStateAction<MetadataViewModel>>,
) => {
  const path = APIPathWithArguments(APIPath.metadata.stats, { id: model.id });
  return apiClientWithoutHandler.genericController.get<MetadataResponse>(path).then((res) => {
    if (res.ok) {
      const response = res.data;
      if (response) {
        setStatistics(mapMetadataViewModel(response, t));
      }
    }
    return res;
  });
};

export const resetMetadataStatistics = (
  model: MetadataViewModel,
  t: TFunction,
  setStatistics: Dispatch<SetStateAction<MetadataViewModel>>,
) => {
  const path = APIPathWithArguments(APIPath.metadata.stats, { id: model.id });
  return apiClientWithoutHandler.genericController.delete<MetadataResponse>(path).then((res) => {
    if (res.ok) {
      const response = res.data;
      if (response) {
        setStatistics(mapMetadataViewModel(response, t));
      }
    }
    return res;
  });
};

export const createMetadata = (model: MetadataViewModel, t: TFunction) => {
  return apiClientWithoutHandler.genericController
    .post<MetadataResponse>(APIPath.metadata.add, model)
    .then((res) => {
      if (res.ok) {
        const response = res.data;
        if (response) {
          const viewModel = mapMetadataViewModel(response, t);
          const metadata = [...metadataModels];
          metadata.splice(0, 0, { ...viewModel, selected: false });
          saveResponseToMetadata(metadata);
        }
      }
      return res;
    })
    .then((res) => {
      notificationHandler(res, t('metadata.notifications.saveSuccessMsg'), t('metadata.notifications.saveErrorMsg'), t);
      return res;
    });
};

export const updateMetadata = (model: MetadataViewModel, t: TFunction) => {
  const path = APIPathWithArguments(APIPath.metadata.update, { id: model.id });
  return apiClientWithoutHandler.genericController
    .put<MetadataResponse>(path, model)
    .then((res) => {
      if (res.ok) {
        const response = res.data;
        if (response) {
          if (response.stats && model.stats.sensorUIDs && model.stats.sensorUIDs.length > 0) {
            response.stats.sensorUIDs = model.stats.sensorUIDs; // keep sensor list from before PUT (WALU-149)
          }
          replaceViewModel(response, t);
        }
      }
      return res;
    })
    .then((res) => {
      notificationHandler(
        res,
        t('metadata.notifications.changeSuccessMsg'),
        t('metadata.notifications.changeErrorMsg'),
        t,
      );
      return res;
    });
};

export const deleteMetadata = (model: MetadataViewModel) => {
  const path = APIPathWithArguments(APIPath.metadata.delete, { id: model.id });
  return apiClientWithoutHandler.genericController.delete(path).then((res) => {
    if (res.ok) {
      deleteViewModel(model);
    }
    return res;
  });
};

export const validateMetadataHostname = (hostname: string, t: TFunction): Promise<any> => {
  return apiClientWithoutHandler.genericController
    .post<GenericResponse>(APIPath.hostnamevalidate.stream, { hostname: hostname })
    .then((res) => {
      if (res.ok) {
        const response = res.data;
        if (!response.success) {
          createAndDispatchNotification(
            t('metadata.notifications.invalidIpAddressErrorMsg'),
            NotificationVariant.WARNING,
            t,
          );
        }
      }
      return res;
    });
};
